import React from "react";

import { EVENTS } from "../../data/content";
import { postRequest } from "../../utils/apiHandlers";

const EMPTY = {
  fullName: "",
  email: "",
  phone: "",
  guests: "",
  eventType: "",
  eventDate: "",
  startTime: "",
  endTime: "",
  catering: "",
  conferencing: "",
  website: "", //Honeypot
};

const today = () => new Date().toISOString().slice(0, 10);

//"ENQUIRE" form on the Private Events page. presetType comes from the event type buttons higher up the page
const EventEnquiryForm = ({ presetType, firstFieldRef }) => {
  const [form, setForm] = React.useState(EMPTY);
  const [isSending, setIsSending] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");
  const [sentTo, setSentTo] = React.useState(null);

  React.useEffect(() => {
    if (presetType) {
      setForm((f) => ({ ...f, eventType: presetType.type }));
    }
  }, [presetType]);

  const update = (prop) => (e) => setForm({ ...form, [prop]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");
    setIsSending(true);
    try {
      await postRequest("/Contact/SendEventEnquiry", form);
      setSentTo(form.fullName.split(" ")[0]);
      setForm(EMPTY);
    }
    catch (err) {
      setErrorText(err.message);
    }
    setIsSending(false);
  };

  if (sentTo !== null) {
    return (
      <div
        className={"event-form-done"}
        role={"status"}
      >
        <p className={"body-text"}>Thank you{sentTo ? `, ${sentTo}` : ""}! Your enquiry has been sent and our events team will be in touch soon.</p>
        <p className={"body-text event-form-note"}>{EVENTS.VIEWINGS}</p>
        <button
          type={"button"}
          className={"btn"}
          onClick={() => setSentTo(null)}
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      className={"event-form"}
      onSubmit={onSubmit}
    >
      <label className={"event-field"}>
        <span className={"visually-hidden"}>Full Name (required)</span>
        <input
          ref={firstFieldRef}
          type={"text"}
          value={form.fullName}
          onChange={update("fullName")}
          required
          maxLength={200}
          autoComplete={"name"}
          placeholder={"Full Name"}
        />
      </label>
      <label className={"event-field"}>
        <span className={"visually-hidden"}>Email (required)</span>
        <input
          type={"email"}
          value={form.email}
          onChange={update("email")}
          required
          maxLength={200}
          autoComplete={"email"}
          placeholder={"Email"}
        />
      </label>
      <label className={"event-field"}>
        <span className={"visually-hidden"}>Phone Number (required)</span>
        <input
          type={"tel"}
          value={form.phone}
          onChange={update("phone")}
          required
          maxLength={50}
          autoComplete={"tel"}
          placeholder={"Phone Number"}
        />
      </label>
      <label className={"event-field"}>
        <span className={"visually-hidden"}>Number of Guests</span>
        <input
          type={"number"}
          value={form.guests}
          onChange={update("guests")}
          min={1}
          max={1000}
          inputMode={"numeric"}
          placeholder={"Number of Guests"}
        />
      </label>
      <label className={"event-field"}>
        <span className={"visually-hidden"}>Event Type</span>
        <select
          value={form.eventType}
          onChange={update("eventType")}
          className={form.eventType ? "" : "is-placeholder"}
        >
          <option value={""}>Event Type</option>
          {EVENTS.EVENT_TYPES.map((type) => (
            <option
              key={type}
              value={type}
            >
              {type}
            </option>
          ))}
          <option value={"Other"}>Other</option>
        </select>
      </label>
      <label className={"event-field has-label"}>
        <span className={"event-label"}>Event Date</span>
        <input
          type={"date"}
          value={form.eventDate}
          onChange={update("eventDate")}
          min={today()}
        />
      </label>

      <div className={"event-times"}>
        <label className={"event-field has-label"}>
          <span className={"event-label"}>Event Start Time (From 7am)</span>
          <input
            type={"time"}
            value={form.startTime}
            onChange={update("startTime")}
            min={"07:00"}
            max={"22:00"}
            step={900}
          />
        </label>
        <label className={"event-field has-label"}>
          <span className={"event-label"}>Event End Time (Till 10pm)</span>
          <input
            type={"time"}
            value={form.endTime}
            onChange={update("endTime")}
            min={"07:00"}
            max={"22:00"}
            step={900}
          />
        </label>
      </div>

      <div className={"event-times"}>
        <label className={"event-field has-label"}>
          <span className={"event-label"}>A La Carte, Buffet Menu, Spit Braai or Platter?</span>
          <select
            value={form.catering}
            onChange={update("catering")}
            className={form.catering ? "" : "is-placeholder"}
          >
            <option value={""}>Choose an option</option>
            {EVENTS.CATERING_OPTIONS.map((option) => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            ))}
          </select>
        </label>
        <label className={"event-field has-label"}>
          <span className={"event-label"}>Conferencing</span>
          <select
            value={form.conferencing}
            onChange={update("conferencing")}
            className={form.conferencing ? "" : "is-placeholder"}
          >
            <option value={""}>Choose an option</option>
            {EVENTS.CONFERENCING_OPTIONS.map((option) => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label
        className={"form-honeypot"}
        aria-hidden={"true"}
      >
        Website
        <input
          type={"text"}
          value={form.website}
          onChange={update("website")}
          tabIndex={-1}
          autoComplete={"off"}
        />
      </label>

      <p className={"event-form-note"}>{EVENTS.VIEWINGS}</p>

      {errorText ? (
        <p
          className={"form-message is-error"}
          role={"alert"}
        >
          {errorText}
        </p>
      ) : null}

      <button
        type={"submit"}
        className={"btn event-submit"}
        disabled={isSending}
      >
        {isSending ? "Sending..." : "Submit"}
      </button>
    </form>
  );
};

export default EventEnquiryForm;
